import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { ModuleRegistry } from '@ag-grid-community/core/dist/esm/es6/modules/moduleRegistry';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model/dist/esm/es6/serverSideRowModelModule';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter/dist/esm/es6/setFilterModule';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar/dist/esm/es6/statusBarModule';

import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, switchMap } from 'rxjs';
import { BaseGrid } from 'shared/abstract/base-grid';
import { DateFloatingFilterComponent } from 'shared/components/ag-grid/filters/date-floating-filter/date-floating-filter.component';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { AccessTypeEnum, PermissionAccessZoneEnum } from 'shared/interfaces/permissions.interface';
import { EventBusService } from 'shared/services/event-bus.service';
import { ModalService } from 'shared/services/modal.service';
import { ToastService } from 'shared/services/toast.service';
import { UserApiService } from 'shared/services/userApi.service';
import { supplierUserHasAccess } from 'shared/util/supplierUserHasAccess';
import { CreateAccessGroupComponent } from 'users/components/access-groups/create-access-group/create-access-group.component';
import { EditAccessGroupComponent } from 'users/components/access-groups/edit-access-group/edit-access-group.component';
import { AccessGroup } from 'users/interfaces/access-group.interface';
import { AccessGroupsService } from 'users/services/access-groups.service';

@Component({
    selector: 'app-access-groups-table',
    templateUrl: './access-groups-table.component.html',
    styleUrls: ['./access-groups-table.component.scss'],
    imports: [AgGridModule, TranslateModule],
})
export class AccessGroupsTableComponent extends BaseGrid implements OnInit, OnDestroy {
    public userType!: string;

    //TODO column headers should be translated as well
    public columnDefs: ColDef[] = [
        {
            field: 'companyName',
            unSortIcon: true,
            floatingFilter: true,
            hide: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            width: 180,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'name',
            headerName: 'Group Name',
            unSortIcon: true,
            floatingFilter: true,
            width: 180,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'entityTracking.createdBy',
            headerName: 'Created By',
            sortable: false,
            width: 150,
        },
        {
            field: 'entityTracking.createdAt',
            headerName: 'Created At',
            colId: 'createdAt',
            sortable: false,
            width: 150,
            floatingFilter: true,
            floatingFilterComponent: DateFloatingFilterComponent,
            floatingFilterComponentParams: { suppressFilterButton: true },
            valueFormatter: this.dateFormatter,
        },
        { field: 'entityTracking.modifiedBy', headerName: 'Modified By', sortable: false, width: 150 },
        {
            field: 'entityTracking.modifiedAt',
            headerName: 'Modified At',
            colId: 'modifiedAt',
            sortable: false,
            width: 150,
            floatingFilter: true,
            floatingFilterComponent: DateFloatingFilterComponent,
            floatingFilterComponentParams: { suppressFilterButton: true },
            valueFormatter: this.dateFormatter,
        },
        {
            colId: 'actions',
            filter: false,
            resizable: false,
            sortable: false,
            minWidth: 110,
            maxWidth: 110,
            headerComponent: 'resetFiltersHeader',
            cellRenderer: 'actionButtonsCellRenderer',
            cellRendererParams: {
                buttons: [
                    {
                        clickAction: this.deleteAccessGroupButtonClickHandler.bind(this),
                        icon: 'trash',
                        computeDisabledState: this.isDeleteButtonDisabled.bind(this),
                        computeTooltip: this.computeTooltip.bind(this),
                    },
                    {
                        clickAction: this.editAccessGroupButtonClickHandler.bind(this),
                        icon: 'pen-to-square',
                        tooltip: 'generalTooltips.edit',
                    },
                ],
            },
        },
    ];

    constructor(
        router: Router,
        activatedRoute: ActivatedRoute,
        destroyRef: DestroyRef,
        private accessGroupsService: AccessGroupsService,
        private translateService: TranslateService,
        private modalService: ModalService,
        private toastService: ToastService,
        private eventBusService: EventBusService,
        public userApiService: UserApiService,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(accessGroupsService, router, activatedRoute, destroyRef);
    }

    public ngOnInit(): void {
        //TODO: extract this in a general method
        this.subscriptions.getLoginInfo = this.userApiService.getLoginInformation().subscribe((userInformation) => {
            if (userInformation.isShiji) {
                this.columnDefs[0].hide = false;
                this.agGrid?.api?.setColumnVisible('companyName', true);
            } else {
                //supplier user should have at least "Edit" permissions
                userInformation = userInformation as CurrentSupplierUserInterface;

                const actionsColumn = this.columnDefs.find((x) => x.colId == 'actions') as ColDef;
                const shouldShowActions = supplierUserHasAccess(
                    userInformation.internalPermissions,
                    PermissionAccessZoneEnum.ACCESS_TO_LISTINGS_ACCESS_GROUPS,
                    AccessTypeEnum.EDIT,
                );

                if (!shouldShowActions) {
                    actionsColumn.cellRenderer = null;
                    actionsColumn.maxWidth = 60;
                }
            }
        });

        this.subscriptions.pageMainAction = this.eventBusService.getObservableForEventStream('pageMainButtonAction').subscribe((data) => {
            if (data === 'accessGroups') {
                this.openCreateAccessGroupModal();
            }
        });
    }

    private editAccessGroupButtonClickHandler(rowData: AccessGroup) {
        const modalData = { ...rowData, type: 'side' };
        const reference = this.modalService.openModal(EditAccessGroupComponent, modalData);

        this.subscriptions.editAccessGroupButton = reference.onClose.subscribe((isSaved) => {
            if (isSaved) {
                this.agGrid?.api.refreshServerSide({ purge: true });
            }
        });
    }

    private deleteAccessGroupButtonClickHandler(rowData: AccessGroup) {
        this.subscriptions.deleteAccessGroupModal = this.modalService
            .openDeleteConfirmationModal(
                this.translateService.instant('users.accessGroups.deleteModal.body'),
                this.translateService.instant('users.accessGroups.deleteModal.title'),
                rowData.companyName,
                rowData.name,
                this.translateService.instant('users.accessGroups.deleteModal.entityTypeName'),
            )
            .onClose.pipe(
                filter(Boolean),
                switchMap(() => {
                    return this.accessGroupsService.deleteListingAccessGroupById(rowData.id);
                }),
            )
            .subscribe(() => {
                this.toastService.showToast('users.accessGroups.deleteSuccess', 'success', { groupName: rowData.name });
                this.agGrid?.api.refreshServerSide({ purge: true });
            });
    }

    private isDeleteButtonDisabled(rowData: any): boolean {
        return this.isDeleteDisabledForRow(rowData);
    }

    private computeTooltip(rowData: any) {
        return this.isDeleteDisabledForRow(rowData) ? 'users.accessGroups.deleteGroupWithUsersTooltip' : 'generalTooltips.delete';
    }

    private isDeleteDisabledForRow(rowData: any): boolean {
        return rowData.users?.length > 0;
    }

    private openCreateAccessGroupModal() {
        this.modalService
            .openModal(CreateAccessGroupComponent, { type: 'side' })
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isSaved) => {
                if (isSaved) {
                    if (this.agGrid) {
                        this.agGrid?.api.refreshServerSide({ purge: true });
                    } else {
                        this.anyRows.set(true);
                    }
                }
            });
    }
}
